import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowLeftOutlined from '@mui/icons-material/KeyboardArrowLeftOutlined';
import KeyboardArrowRightOutlined from '@mui/icons-material/KeyboardArrowRightOutlined';
import { Button, Grid, Modal, styled, Typography } from '@mui/material';
import MuiCircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import type { FetcherWithComponents } from '@remix-run/react';
import { useFetcher } from '@remix-run/react';
import * as React from 'react';
import Image from 'remix-image';
import Colors from '~/components/shared/Colors';
import NoImageAvailable from '~/images/no_image_available.svg';
import type { Pack } from '~/routes/build-pack/create';
import { packActions } from '~/routes/build-pack/create';
import type { Attribute, Color } from '~/services/api-utils.server';
import type { ItemAttributes, PackItem, PackItemPrice, PackTemplate, Product } from '~/services/api.server';
import { getEmptyArray, getFormattedPrice, isErrorResponse, isPackTemplate } from '~/utils/common';
import { scrollbarStyle } from '~/utils/common-styles';
import { defaultAttributes } from '~/utils/designLab';
import { pluralToSingular, validCombination } from '~/utils/validCombination';
import { MuiRemixLink } from './links';
import { PackBadge } from './Packs';
import SeeMoreDescription from './SeeMoreDescription';

export function createSearchParamsWithAttribute(product: Product, attribute?: ProductAttribute) {
  const attributes = Object.keys(product.available_attributes);
  const combination = Object.values(validCombination(product) || {});
  let params = new URLSearchParams();

  params.set('productSlug', product.slug);

  if (attribute) {
    for (let i = 0; i < combination.length; i++) {
      if (attributes[i] === Object.keys(attribute)?.[0]) {
        params.set(pluralToSingular(String(Object.keys(attribute)?.[0])), Object.values(attribute)?.[0]);
      } else {
        params.set(pluralToSingular(String(attributes[i])), combination[i]);
      }
    }
  } else {
    for (let i = 0; i < combination.length; i++) {
      params.set(pluralToSingular(String(attributes[i])), combination[i]);
    }
  }

  return params;
}

type ProductAttribute = {
  colors: string;
};

function getAvailableSizes(product: Product, selectedColor: string) {
  const sizes = product.available_attributes.sizes;
  if (sizes) {
    const unavailableSizes = product.unavailable_variants;
    let availableSizes = sizes.map((size) => size.name);
    const unavailable: Array<string> = [];

    unavailableSizes.forEach((variant) => {
      const colorSize = variant.split(',');
      if (selectedColor == colorSize[0]) {
        unavailable.push(colorSize[1]);
      }
    });

    availableSizes = availableSizes.filter((size) => !unavailable.includes(size));
    return availableSizes;
  }
  return [];
}

type ProductModalProps = {
  product: Product | null;
  pack: Pack;
  isPackaging: boolean;
  onClose: () => void;
};
export default function ProductModal({ product, pack, isPackaging, onClose }: ProductModalProps) {
  const fetcher = useFetcher();

  return (
    <Modal
      open={!!product}
      onClose={onClose}
      sx={{
        display: 'flex',
        overflowY: { xs: 'auto' },
        alignItems: { md: 'center' },
        justifyContent: 'center',
      }}
      keepMounted
    >
      <fetcher.Form method="post" action="/build-pack/create" onSubmit={onClose}>
        {product && !isPackTemplate(product) && (
          <>
            <input type="hidden" name="product" value={JSON.stringify(product)} />
            <ProductForm product={product} pack={pack} isPackaging={isPackaging} onClose={onClose} />
          </>
        )}
        {product && isPackTemplate(product) && (
          <>
            <input type="hidden" name={'packTemplateId'} value={product.id} />
            <PackTemplateForm product={product} pack={pack} onClose={onClose} />
          </>
        )}
      </fetcher.Form>
    </Modal>
  );
}
const StyledSelect = styled(Select)({
  marginLeft: '8px',
  lineHeight: '1.25rem',
  '& .MuiSelect-select': { padding: '0 20px 0 8px !important', fontSize: 12 },
  '& .MuiSelect-icon': { right: 0, fontSize: 20 },
  '& input': { padding: 0, margin: 0, border: 'none' },
});
type ProductFormProps = {
  product: Product;
  pack: Pack;
  isPackaging: boolean;
  onClose: () => void;
};
function ProductForm({ product, pack, isPackaging, onClose }: ProductFormProps) {
  const imagesFetcher = useFetcher();
  const priceFetcher = useFetcher();

  const [mainImage, setMainImage] = React.useState<string>();
  const [secondaryImages, setSecondaryImages] = React.useState<Array<string>>([]);
  const [currentPage, setCurrentPage] = React.useState(1);

  const color = pack.items_attributes[product.id]?.attributes?.find((attribute) => attribute.name === 'color')?.value;
  const [selectedColor, setSelectedColor] = React.useState<string>(
    color ? color : product.available_attributes.colors?.[0].name
  );
  const [prices, setPrices] = React.useState<number>();
  const [quantityChanged, setQuantityChanged] = React.useState(false);
  const [quantity, setQuantity] = React.useState(pack.items_units_per_pack[product.id] ?? 1);

  //  Get images useEffect
  React.useEffect(() => {
    if (imagesFetcher.type === 'init') {
      const params = createSearchParamsWithAttribute(product, { colors: selectedColor }).toString();
      imagesFetcher.load(`/api/product-part-images?${params}`);
    }
  }, [imagesFetcher, product, selectedColor]);

  React.useEffect(() => {
    setQuantityChanged(true);
  }, [quantity]);

  //  Get prices useEffect
  React.useEffect(() => {
    if (priceFetcher.type === 'init' || quantityChanged) {
      setQuantityChanged(false);
      const params = createSearchParamsWithAttribute(product, { colors: selectedColor });
      params.set('quantity', String(quantity * pack.quantity));
      priceFetcher.load(`/api/product-prices?${params.toString()}`);
    }
  }, [priceFetcher, product, quantity, quantityChanged, pack.quantity, selectedColor]);

  // Set images useEffect
  React.useEffect(() => {
    if (imagesFetcher.type === 'done' && imagesFetcher.data) {
      setMainImage(imagesFetcher.data.productImage || imagesFetcher.data.blankImage);
      //  Handle product media_content in setSecondaryImages
      setSecondaryImages([imagesFetcher.data.productImage || imagesFetcher.data.blankImage]);
    }
  }, [imagesFetcher.data, imagesFetcher.type]);

  //  Set prices useEffect
  React.useEffect(() => {
    const props = priceFetcher.data;
    if (priceFetcher.type === 'done' && props) {
      setPrices(props.prices.prices.total.amount);
    }
  }, [priceFetcher]);

  function AddButtons() {
    return (
      <Stack mt={2} mb={{ xs: 2, md: 0 }}>
        {isPackaging ? (
          pack.container?.id === product.id || pack.filler?.id === product.id ? (
            <Button
              variant="contained"
              size="large"
              type="submit"
              name="_action"
              value={pack.container?.id === product.id ? packActions.setContainer : packActions.setFiller}
            >
              UPDATE
            </Button>
          ) : (
            <Button
              variant="contained"
              size="large"
              type="submit"
              name="_action"
              value={product.can_be_container ? packActions.setContainer : packActions.setFiller}
              sx={{ width: '100%' }}
            >
              {product.can_be_container ? 'ADD PACKAGING' : 'ADD FILLER'}
            </Button>
          )
        ) : productAlreadyInPack(product, pack) ? (
          <Button
            variant="contained"
            size="large"
            type="submit"
            name="_action"
            value={packActions.addProduct}
            sx={{
              width: '100%',
            }}
          >
            UPDATE
          </Button>
        ) : (
          <Button
            variant="contained"
            size="large"
            type="submit"
            name="_action"
            value={packActions.addProduct}
            sx={{
              width: '100%',
            }}
          >
            ADD TO PACK
          </Button>
        )}
      </Stack>
    );
  }

  function handleAttribute(attributeName: string | number, attributeProps: Attribute[]) {
    const handleChangeColor = (event: React.MouseEvent<{ value: string }>) => {
      const color = { colors: event.currentTarget.value };
      const params = createSearchParamsWithAttribute(product, color);
      setSelectedColor(event.currentTarget.value);
      // Get image and price
      imagesFetcher.load(`/api/product-part-images?${params.toString()}`);
      params.set('quantity', String(quantity * pack.quantity));
      priceFetcher.load(`/api/product-prices?${params.toString()}`);
    };

    switch (attributeName) {
      case 'colors':
        return (
          <Stack
            key={attributeName}
            sx={{
              '.MuiTypography-subtitle2': {
                alignSelf: 'flex-start',
                fontWeight: 700,
                mb: 1,
              },
            }}
          >
            <Colors
              colors={attributeProps as Color[]}
              onColorChange={handleChangeColor}
              selectedColor={selectedColor}
              unavailableInventoryParts={product.unavailable_inventory_parts}
            />
            <Divider sx={{ my: 2, borderColor: 'primary.main' }} />
          </Stack>
        );

      case 'sizes':
        return (
          <Stack key={attributeName} my={1}>
            <Typography variant="caption" color="text.disabled">
              The following attributes can be selected at checkout:
            </Typography>

            <Stack direction="row" alignItems="center" flexWrap="wrap">
              <Typography variant="body2" mr={{ xs: 1, md: 0 }} alignItems="center" alignSelf="flex-start">
                Sizes:
              </Typography>
              <Stack direction="row" ml={{ md: 1 }} overflow="auto">
                <Typography variant="body2">{getAvailableSizes(product, selectedColor)?.join(', ')}</Typography>
              </Stack>
            </Stack>
            <Divider sx={{ my: 2, borderColor: 'primary.main' }} />
          </Stack>
        );

      case 'book_types':
        return (
          <Stack key={attributeName} my={1}>
            <Typography variant="caption" color="text.disabled">
              The following attributes can be selected at checkout:
            </Typography>

            <Stack flexDirection="row" alignItems="center" flexWrap="wrap">
              <Typography variant="body2" mr={{ xs: 1, md: 0 }}>
                Types:
              </Typography>
              <Stack flexDirection="row" overflow="auto">
                {attributeProps?.map((type) => (
                  <Button
                    key={type.name}
                    variant="outlined"
                    size="small"
                    sx={{
                      px: 1,
                      py: 0.5,
                      ml: 1,
                      minHeight: 0,
                      minWidth: 48,
                    }}
                    name="type"
                    value={type.name}
                  >
                    {type.name}
                  </Button>
                ))}
              </Stack>
            </Stack>
            <Divider sx={{ my: 2, borderColor: 'primary.main' }} />
          </Stack>
        );
    }
  }

  function handleSecondaryImageClick(image: string) {
    if (mainImage != image) {
      setMainImage(image);
    }
  }

  function handleChangeQuantity(event: any) {
    const value = Number(event.target.value);
    setQuantity(value);
  }

  return (
    <>
      <input type="hidden" name="unitsPerPack" value={quantity} />
      <input type="hidden" name="color" value={selectedColor} />

      <Grid
        container
        flexDirection={{ xs: 'column', md: 'row' }}
        flexWrap="nowrap"
        p={{ xs: 2, md: 4 }}
        sx={{
          position: 'relative',
          width: { xs: '100vw', md: 900 },
          height: { md: 656 },
          background: '#FFFFFF',
          boxShadow: '0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)',
          borderRadius: { md: 3 },
          px: 2,
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            width: 'fit-content',
            position: { xs: 'relative', md: 'absolute' },
            top: { md: '0px' },
            right: { xs: '-90%', md: '2px' },
            color: '#667085',
          }}
        >
          <CloseIcon />
        </IconButton>
        {/* Left side */}
        <Grid item xs={12} md={5} lg={5}>
          <Stack
            spacing={{ md: 3 }}
            px={{ xs: 3, md: 0 }}
            justifyContent="center"
            alignItems="center"
            borderRadius={2}
            height="464px"
            bgcolor="#F0F5F9"
            width={{ md: '328px' }}
          >
            {mainImage && (
              <Image
                src={mainImage}
                alt={product.name}
                width={280}
                height={344}
                responsive={[{ size: { width: 280, height: 344 } }]}
                options={{ fit: 'inside' }}
                style={{ maxWidth: '100%', minWidth: 'auto', opacity: imagesFetcher.type !== 'done' ? 0.3 : 1 }}
                dprVariants={[1, 3]}
              />
            )}
            {imagesFetcher.type !== 'done' && (
              <MuiCircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  zIndex: 1,
                }}
              />
            )}
          </Stack>
          <Grid container mt={3} ml={0} width={328} justifyContent="space-between">
            {secondaryImages.length > 4 ? (
              <Stack flexDirection="row" alignItems="center" justifyContent="start" width="100%">
                <IconButton
                  size="large"
                  color="inherit"
                  aria-label="user menu"
                  aria-haspopup="true"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  sx={{ width: 12, height: 12 }}
                >
                  <KeyboardArrowLeftOutlined />
                </IconButton>
                <Grid display="flex" justifyContent="start" width="100%" sx={{ '& > div:nth-of-type(n+2)': { ml: 2 } }}>
                  {secondaryImages.slice((currentPage - 1) * 4, currentPage * 4).map((image) => (
                    <Stack
                      key={image + 'secondary'}
                      bgcolor="#F0F5F9"
                      borderRadius={2}
                      justifyContent="center"
                      alignItems="center"
                      py={1.25}
                      px={1.75}
                      width="60px"
                      height="60px"
                      border={image == mainImage ? `1px solid #009DC8` : '1px solid transparent'}
                      // dont compare with id because all images would have same id in the future
                      sx={{ '&:hover': { border: `1px solid #009DC8`, cursor: 'pointer' } }}
                      onClick={() => handleSecondaryImageClick(image)}
                    >
                      <Image
                        src={image}
                        alt={product.name}
                        responsive={[{ size: { height: 40, width: 32 } }]}
                        options={{ fit: 'inside' }}
                        dprVariants={[1, 3]}
                        width={32}
                        height={40}
                        style={{ width: '100%', minWidth: 'auto', minHeight: 'auto' }}
                      />
                    </Stack>
                  ))}
                </Grid>
                <IconButton
                  size="large"
                  color="inherit"
                  aria-label="user menu"
                  aria-haspopup="true"
                  disabled={Math.ceil(secondaryImages.length / 4) === currentPage}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  sx={{ width: 12, height: 12 }}
                >
                  <KeyboardArrowRightOutlined />
                </IconButton>
              </Stack>
            ) : (
              <Grid display="flex" justifyContent="start" width="100%" sx={{ '& > div:nth-of-type(n+2)': { ml: 2 } }}>
                {secondaryImages.map((image) => (
                  <Stack
                    key={image + 'secondary'}
                    bgcolor="#F0F5F9"
                    borderRadius={2}
                    justifyContent="center"
                    alignItems="center"
                    py={1.25}
                    px={1.75}
                    width="60px"
                    height="60px"
                    border={image == mainImage ? `1px solid #009DC8` : '1px solid transparent'}
                    // dont compare with id because all images would have same id in the future
                    sx={{
                      '&:hover': {
                        border: `1px solid #009DC8`,
                        cursor: 'pointer',
                      },
                    }}
                    onClick={() => handleSecondaryImageClick(image)}
                  >
                    <Image
                      src={image}
                      alt={product.name}
                      responsive={[{ size: { height: 40, width: 32 } }]}
                      options={{ fit: 'inside' }}
                      dprVariants={[1, 3]}
                      width={32}
                      height={40}
                      style={{ width: '100%', minWidth: 'auto', minHeight: 'auto' }}
                    />
                  </Stack>
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid item xs={12} md={7} lg={7}>
          {/* Right side */}
          <Stack height={{ md: 536 }} sx={scrollbarStyle}>
            <Stack pl={2} pr={4} mt={{ xs: 3, md: 0 }}>
              <Typography variant="h6" fontWeight="700">
                {product.name}
              </Typography>
              {product.description ? <SeeMoreDescription description={product.description} /> : null}
            </Stack>

            <Stack bgcolor="#F0F5F9" borderRadius={2} mt={{ md: 3 }} height="100%">
              <Stack bgcolor="#FFFFFF" alignItems="flex-start" m={2} borderRadius={3} height="100%">
                <Grid container sx={{ m: 0, pt: 3, pb: 2, px: 2, height: '100%' }}>
                  <Stack justifyContent="space-between" width="100%">
                    <Stack width="100%">
                      {Object.keys(product.available_attributes).length > 0 &&
                        Object.keys(product.available_attributes).map((attribute) => {
                          return handleAttribute(attribute, product.available_attributes[attribute]);
                        })}
                    </Stack>
                    {/* Total packs */}
                    <Stack>
                      <Stack
                        flexDirection="row"
                        pl={1}
                        width="100%"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap="wrap"
                      >
                        <Stack flexDirection="row">
                          <Typography variant="body2">Qty per pack:</Typography>
                          <StyledSelect value={quantity} onChange={handleChangeQuantity}>
                            {getEmptyArray(10).map((value) => (
                              <MenuItem key={value} value={value}>
                                {value}
                              </MenuItem>
                            ))}
                          </StyledSelect>
                        </Stack>

                        <Typography variant="body2" width={{ xs: 'max-content' }} mt={{ xs: 1, sm: 0 }}>
                          Total packs: {pack.quantity}
                        </Typography>
                      </Stack>
                      {/* Estimated price */}
                      <Stack
                        flexDirection="row"
                        justifyContent="space-between"
                        alignItems={'center'}
                        width="100%"
                        pl={1}
                      >
                        <Typography variant="body2" mt={1.25} fontWeight="500">
                          Estimated price:
                        </Typography>

                        <Typography
                          variant="body2"
                          mt={1.25}
                          sx={{
                            fontWeight: 500,
                            opacity: priceFetcher.type !== 'done' || imagesFetcher.type !== 'done' ? 0.3 : 1,
                          }}
                        >
                          {priceFetcher.type !== 'done' || imagesFetcher.type !== 'done' ? (
                            <MuiCircularProgress
                              size={12}
                              sx={{
                                mr: 0.5,
                              }}
                            />
                          ) : null}
                          {prices ? getFormattedPrice(prices / 100) : '$0.00'}
                        </Typography>
                      </Stack>
                      <Typography color="#9E9E9E" mt={1} ml={1} fontSize={12}>
                        Price based on 1 color and 1 decoration location.
                      </Typography>
                    </Stack>
                  </Stack>
                </Grid>
              </Stack>
            </Stack>
          </Stack>
          <AddButtons />
        </Grid>
      </Grid>
    </>
  );
}

function PackTemplateForm({ product, pack, onClose }: { product: PackTemplate; pack: Pack; onClose: () => void }) {
  const productFetcher = useFetcher();
  const priceFetcher = useFetcher();
  const [packTemplate, setPackTemplate] = React.useState<PackTemplate | null>(null);
  const [mainImage, setMainImage] = React.useState<string>(product.image || NoImageAvailable);
  const [secondaryImages, setSecondaryImages] = React.useState<Map<number, string>>(new Map());
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsColorAttributes, setItemsColorAttribute] = React.useState<Record<number, string>>({});
  const selectedItemsAttributes = React.useRef(packTemplate ? defaultAttributes(packTemplate) : null);
  const [prices, setPrices] = React.useState<{ prices: PackItemPrice[] }>();

  //  Get packTemplate (product doesn't have items)
  React.useEffect(() => {
    if (productFetcher.type === 'init') {
      let params = new URLSearchParams();
      params.set('productId', product.id.toString());
      productFetcher.load(`/api/product?${params}`);
    }
  }, [productFetcher, product]);

  // Set packTemplate and images
  React.useEffect(() => {
    if (productFetcher.type === 'done' && !isErrorResponse(productFetcher.data)) {
      const packTemplate = productFetcher.data;
      setPackTemplate(packTemplate);
      // set Images if empty
      if (secondaryImages.size === 0) {
        const map1: Map<number, string> = new Map();
        map1.set(packTemplate.id, packTemplate.image);
        map1.set(packTemplate.container.id, packTemplate.container.image);
        if (packTemplate.filler) {
          map1.set(packTemplate.filler.id, packTemplate.filler.image);
        }
        packTemplate.items.forEach((item: PackItem) => {
          if (item.product?.image) {
            map1.set(item.product.id, item.product.image);
          }
        });

        setSecondaryImages(map1);
      }
      //  Get prices
      selectedItemsAttributes.current = defaultAttributes(productFetcher.data);
      if (priceFetcher.type === 'init') {
        priceFetcher.submit(
          {
            packAttributes: JSON.stringify(defaultAttributes(productFetcher.data)),
            product: JSON.stringify(packTemplate),
            quantity: pack.quantity.toString(),
          },
          { method: 'post', action: '/api/pack-template-prices' }
        );
      }
    }
  }, [productFetcher.data, productFetcher.type, secondaryImages.size, priceFetcher, pack.quantity]);

  //  Set prices useEffect
  React.useEffect(() => {
    if (priceFetcher.type === 'done' && priceFetcher.data) {
      setPrices(priceFetcher.data);
    }
  }, [priceFetcher]);

  function handleSecondaryImageClick(image: string) {
    if (mainImage != image) {
      setMainImage(image);
    }
  }

  return (
    <>
      <input type="hidden" name="itemsColor" value={JSON.stringify(itemsColorAttributes)} />
      <Grid
        container
        flexDirection={{ xs: 'column', md: 'row' }}
        flexWrap="nowrap"
        p={{ xs: 2, md: 4 }}
        sx={{
          position: 'relative',
          width: { xs: '100vw', md: 900 },
          height: { md: 656 },
          background: '#FFFFFF',
          boxShadow: '0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)',
          borderRadius: { md: 3 },
          px: 2,
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            width: 'fit-content',
            position: { xs: 'relative', md: 'absolute' },
            top: { md: '0px' },
            right: { xs: '-90%', md: '2px' },
            color: '#667085',
          }}
        >
          <CloseIcon />
        </IconButton>
        {/* Left side */}
        <Grid item xs={12} md={5} lg={5}>
          <Stack
            spacing={{ md: 3 }}
            px={{ xs: 3, md: 0 }}
            justifyContent="center"
            alignItems="center"
            borderRadius={2}
            height="464px"
            bgcolor="#F0F5F9"
            width={{ md: '328px' }}
          >
            <Image
              src={mainImage || NoImageAvailable}
              alt={product.name}
              width={280}
              height={344}
              responsive={[{ size: { width: 280, height: 344 } }]}
              options={{ fit: 'inside' }}
              style={{ maxWidth: '100%', minWidth: 'auto' }}
              dprVariants={[1, 3]}
            />
          </Stack>
          <Grid container mt={3} ml={0} width={328} justifyContent="space-between">
            {secondaryImages.size === 0 && productFetcher.state !== 'idle' && (
              <MuiCircularProgress
                size={24}
                sx={{
                  margin: '16px auto',
                  zIndex: 1,
                }}
              />
            )}
            {secondaryImages.size > 4 ? (
              <Stack flexDirection="row" alignItems="center" justifyContent="space-evenly" width="100%">
                <IconButton
                  size="large"
                  color="inherit"
                  aria-label="user menu"
                  aria-haspopup="true"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  sx={{ width: 12, height: 12 }}
                >
                  <KeyboardArrowLeftOutlined />
                </IconButton>
                <Grid
                  display="flex"
                  justifyContent="start"
                  width="100%"
                  sx={{
                    '& > div:nth-of-type(n+2)': {
                      ml: 2,
                    },
                  }}
                >
                  {Array.from(secondaryImages.values())
                    .slice((currentPage - 1) * 4, currentPage * 4)
                    .map((image) => (
                      <Stack
                        key={image + 'secondary'}
                        bgcolor="#F0F5F9"
                        borderRadius={2}
                        justifyContent="center"
                        alignItems="center"
                        py={1.25}
                        px={1.75}
                        width="60px"
                        height="60px"
                        border={image == mainImage ? `1px solid #009DC8` : '1px solid transparent'}
                        // dont compare with id because all images would have same id in the future
                        sx={{
                          '&:hover': {
                            border: `1px solid #009DC8`,
                            cursor: 'pointer',
                          },
                        }}
                        onClick={() => handleSecondaryImageClick(image)}
                      >
                        <Image
                          src={image}
                          alt={product.name}
                          responsive={[{ size: { height: 40, width: 32 } }]}
                          options={{ fit: 'inside' }}
                          dprVariants={[1, 3]}
                          width={32}
                          height={40}
                          style={{ width: '100%', minWidth: 'auto', minHeight: 'auto' }}
                        />
                      </Stack>
                    ))}
                </Grid>
                <IconButton
                  size="large"
                  color="inherit"
                  aria-label="user menu"
                  aria-haspopup="true"
                  disabled={Math.ceil(Array.from(secondaryImages.values()).length / 4) === currentPage}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  sx={{ width: 12, height: 12 }}
                >
                  <KeyboardArrowRightOutlined />
                </IconButton>
              </Stack>
            ) : (
              <Grid display="flex" justifyContent="space-between" width="100%">
                {Array.from(secondaryImages.values()).map((image) => (
                  <Stack
                    key={image + 'secondary'}
                    bgcolor="#F0F5F9"
                    borderRadius={2}
                    justifyContent="center"
                    alignItems="center"
                    py={1.25}
                    px={1.75}
                    width="60px"
                    height="60px"
                    border={image == mainImage ? `1px solid #009DC8` : '1px solid transparent'}
                    // dont compare with id because all images would have same id in the future
                    sx={{
                      '&:hover': {
                        border: `1px solid #009DC8`,
                        cursor: 'pointer',
                      },
                    }}
                    onClick={() => handleSecondaryImageClick(image)}
                  >
                    <Image
                      src={image}
                      alt={product.name}
                      responsive={[{ size: { height: 40, width: 32 } }]}
                      options={{ fit: 'inside' }}
                      dprVariants={[1, 3]}
                      width={32}
                      height={40}
                      style={{ width: '100%', minWidth: 'auto', minHeight: 'auto' }}
                    />
                  </Stack>
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid item xs={12} md={7} lg={7}>
          {/* Right side */}
          <Stack height={{ md: 536 }} sx={scrollbarStyle}>
            <Stack pl={2} pr={4} mt={{ xs: 3, md: 0 }}>
              <Typography variant="h6" fontWeight="700">
                {product.name}
              </Typography>
              {product.description ? <SeeMoreDescription description={product.description} /> : null}
            </Stack>

            <Stack bgcolor="#F0F5F9" borderRadius={2} mt={{ md: 3 }}>
              {!packTemplate && (
                <MuiCircularProgress
                  size={24}
                  sx={{
                    margin: '16px auto',
                    zIndex: 1,
                  }}
                />
              )}
              <Stack
                alignItems="flex-start"
                m={2}
                borderRadius={3}
                sx={{
                  '> div + div': {
                    mt: 1,
                  },
                }}
              >
                {packTemplate && selectedItemsAttributes !== null && selectedItemsAttributes.current !== null && (
                  <>
                    <ShowPack
                      key={packTemplate.container.id}
                      item={packTemplate.container}
                      container
                      pack={pack}
                      prices={prices}
                      setSecondaryImages={setSecondaryImages}
                      setItemsColorAttribute={setItemsColorAttribute}
                      priceFetcher={priceFetcher}
                      selectedItemsAttributes={selectedItemsAttributes}
                      packTemplate={packTemplate}
                      secondaryImages={secondaryImages}
                      mainImage={mainImage}
                      setMainImage={setMainImage}
                    />

                    {selectedItemsAttributes && packTemplate.filler ? (
                      <ShowPack
                        key={packTemplate.filler.id}
                        item={packTemplate.filler}
                        filler
                        pack={pack}
                        setSecondaryImages={setSecondaryImages}
                        setItemsColorAttribute={setItemsColorAttribute}
                        prices={prices}
                        priceFetcher={priceFetcher}
                        selectedItemsAttributes={selectedItemsAttributes}
                        packTemplate={packTemplate}
                        secondaryImages={secondaryImages}
                        mainImage={mainImage}
                        setMainImage={setMainImage}
                      />
                    ) : null}

                    {selectedItemsAttributes &&
                      packTemplate.items?.map((item: PackItem) => {
                        return (
                          <ShowPack
                            key={item.product?.id}
                            item={item}
                            pack={pack}
                            setSecondaryImages={setSecondaryImages}
                            setItemsColorAttribute={setItemsColorAttribute}
                            prices={prices}
                            priceFetcher={priceFetcher}
                            selectedItemsAttributes={selectedItemsAttributes}
                            packTemplate={packTemplate}
                            secondaryImages={secondaryImages}
                            mainImage={mainImage}
                            setMainImage={setMainImage}
                          />
                        );
                      })}
                  </>
                )}
              </Stack>
            </Stack>
          </Stack>
          <Stack mt={2} mb={{ xs: 2, md: 0 }}>
            <Button
              variant="contained"
              size="large"
              type="submit"
              name="_action"
              value={packActions.addPackTemplate}
              sx={{
                width: '100%',
              }}
            >
              SELECT
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </>
  );
}
export function itemAlreadyInPack(product: Product, pack: Pack) {
  //  Any product can be inside Items
  const possibleCategories = ['products'];
  //  Could search in pack.contaner/filler/products without the if's
  if (product.can_be_filler) {
    possibleCategories.push('filler');
  }
  if (product.can_be_container) {
    possibleCategories.push('container');
  }
  if (possibleCategories.includes('container') && pack.container?.id == product.id) {
    return true;
  }

  if (possibleCategories.includes('filler') && pack.filler?.id == product.id) {
    return true;
  }

  const productAlreadyInPack = pack.products.find((packProduct) => packProduct.id == product.id);

  if (productAlreadyInPack) {
    return true;
  }
  //  Item could not be found
  return false;
}

type ShowPackProps = {
  item: PackItem | Product;
  container?: boolean;
  filler?: boolean;
  pack: Pack;
  setSecondaryImages: React.Dispatch<React.SetStateAction<Map<number, string>>>;
  setItemsColorAttribute: React.Dispatch<React.SetStateAction<Record<number, string>>>;
  prices:
    | {
        prices: PackItemPrice[];
      }
    | undefined;
  selectedItemsAttributes: React.MutableRefObject<ItemAttributes[] | null>;
  priceFetcher: FetcherWithComponents<any>;
  packTemplate: PackTemplate | null;
  secondaryImages: Map<number, string>;
  mainImage: string;
  setMainImage: React.Dispatch<React.SetStateAction<string>>;
};

function ShowPack({
  item,
  container = false,
  filler = false,
  pack,
  setSecondaryImages,
  setItemsColorAttribute,
  prices,
  priceFetcher,
  selectedItemsAttributes,
  packTemplate,
  secondaryImages,
  mainImage,
  setMainImage,
}: ShowPackProps) {
  const product = ((item as PackItem).supplier_product || (item as PackItem).product || item) as Product;
  const imagesFetcher = useFetcher();
  const color = pack.items_attributes[product.id]?.attributes?.find((attribute) => attribute.name === 'color')?.value;
  const [selectedColor, setSelectedColor] = React.useState<string>(
    color ? color : product.available_attributes.colors?.[0].name
  );
  const itemPrice = prices?.prices.find((_item) => _item.product === product.id);
  const price = itemPrice?.price?.amount;
  const productImage = secondaryImages.get(product.id);

  // Set images useEffect
  React.useEffect(() => {
    if (imagesFetcher.type === 'done' && imagesFetcher.data) {
      if (mainImage === productImage) {
        setMainImage(imagesFetcher.data.productImage || imagesFetcher.data.blankImage);
      }
      setSecondaryImages((prev) => {
        const updateMap = new Map(prev);
        updateMap.set(product.id, imagesFetcher.data.productImage || imagesFetcher.data.blankImage);
        return updateMap;
      });
    }
  }, [imagesFetcher.data, imagesFetcher.type, product.id, setSecondaryImages, productImage, setMainImage, mainImage]);

  function handleAttribute(attributeName: string | number, attributeProps: Attribute[]) {
    const handleChangeColor = (event: React.MouseEvent<{ value: string }>) => {
      const color = { colors: event.currentTarget.value };
      const params = createSearchParamsWithAttribute(product, color);
      setSelectedColor(event.currentTarget.value);
      setItemsColorAttribute((prev) => ({ ...prev, [product.id]: event.currentTarget.value }));
      // Get image
      imagesFetcher.load(`/api/product-part-images?${params.toString()}`);

      // update item attributes with selected color
      const itemAttribute = selectedItemsAttributes.current?.find(
        (itemAttribute) => itemAttribute.product_id === product.id
      );
      let colorAttribute = itemAttribute?.attributes?.find((attribute) => attribute.name === 'color');
      if (colorAttribute && itemAttribute) {
        colorAttribute.value = event.currentTarget.value;
      }

      priceFetcher.submit(
        {
          packAttributes: JSON.stringify(selectedItemsAttributes.current),
          product: JSON.stringify(packTemplate),
        },
        { method: 'post', action: '/api/pack-template-prices' }
      );
    };

    switch (attributeName) {
      case 'colors':
        return (
          <Stack
            key={attributeName}
            sx={{
              flexDirection: 'row',
              alignItems: 'flex-start',
              '.MuiTypography-subtitle2': {
                flex: '0 0 auto',
                alignSelf: 'auto',
              },
            }}
          >
            <Colors
              colors={attributeProps as Color[]}
              onColorChange={handleChangeColor}
              selectedColor={selectedColor}
              unavailableInventoryParts={product.unavailable_inventory_parts}
            />
          </Stack>
        );

      case 'sizes':
        return (
          <Stack key={attributeName} my={1}>
            <Typography variant="caption" color="text.disabled">
              The following attributes can be selected at checkout:
            </Typography>

            <Stack direction="row" alignItems="center" justifyContent="start" flexWrap="wrap">
              <Typography
                variant="subtitle2"
                color="text.secondary"
                mr={{ xs: 1, md: 0 }}
                alignItems="center"
                alignSelf="flex-start"
              >
                Sizes:
              </Typography>
              <Stack direction="row" ml={{ md: 1 }} overflow="auto">
                <Typography variant="body2">{getAvailableSizes(product, selectedColor)?.join(', ')}</Typography>
              </Stack>
            </Stack>
          </Stack>
        );

      case 'book_types':
        return (
          <Stack key={attributeName} my={1}>
            <Typography variant="caption" color="text.disabled">
              The following attributes can be selected at checkout:
            </Typography>

            <Stack flexDirection="row" alignItems="center" flexWrap="wrap">
              <Typography variant="body2" mr={{ xs: 1, md: 0 }}>
                Types:
              </Typography>
              <Stack flexDirection="row" overflow="auto">
                {attributeProps?.map((type) => (
                  <Button
                    key={type.name}
                    variant="outlined"
                    size="small"
                    sx={{
                      px: 1,
                      py: 0.5,
                      ml: 1,
                      minHeight: 0,
                      minWidth: 48,
                    }}
                    name="type"
                    value={type.name}
                  >
                    {type.name}
                  </Button>
                ))}
              </Stack>
            </Stack>
            <Divider sx={{ my: 2, borderColor: 'primary.main' }} />
          </Stack>
        );
    }
  }

  return (
    <Stack spacing={{ xs: 1, sm: 3 }} bgcolor="#FFF" borderRadius="12px" p={2} width="100%">
      <Stack direction="row" spacing={2}>
        <Stack alignItems="center" spacing={1}>
          <Stack justifyContent="center" alignItems="center" width={74} sx={{ position: 'relative' }}>
            {productImage ? (
              <Image
                src={productImage}
                alt={product.name}
                responsive={[{ size: { width: 64, height: 64 } }]}
                options={{ fit: 'inside' }}
                dprVariants={[1, 3]}
                style={{ width: 64, height: 64, opacity: imagesFetcher.state !== 'idle' ? 0.3 : 1 }}
              />
            ) : (
              <img src={NoImageAvailable} alt="not available" style={{ width: 64, height: 64 }} />
            )}
            {imagesFetcher.state !== 'idle' && (
              <MuiCircularProgress size={25} sx={{ position: 'absolute', zIndex: 1 }} />
            )}
          </Stack>
          {container && <PackBadge text="PACKAGING" />}
          {filler && <PackBadge text="FILLER" />}
        </Stack>
        <Stack spacing={1}>
          <Stack flexDirection="row" alignItems="baseline">
            <MuiRemixLink href={`/shop/${product.slug}`} underline="hover" color="text.primary">
              <Typography variant="subtitle1">{product.name}</Typography>
            </MuiRemixLink>
            &nbsp;
            <Tooltip title="Quantity per Pack" arrow placement="top">
              <Typography variant="body2" color="text.secondary" component="span">
                ({container || filler ? 1 : (item as PackItem).units_per_pack})
              </Typography>
            </Tooltip>
          </Stack>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontWeight: 400,
              opacity: !price ? 0.3 : 1,
            }}
          >
            {!price ? (
              <MuiCircularProgress
                size={12}
                sx={{
                  mr: 0.5,
                }}
              />
            ) : null}
            {price && (
              <Typography
                component="span"
                variant="subtitle2"
                sx={{
                  color: 'text.primary',
                  // fontWeight: 400,
                  opacity: imagesFetcher.state !== 'idle' ? 0.3 : 1,
                }}
              >
                Item price: {getFormattedPrice(price / 100)}
              </Typography>
            )}
          </Typography>
          {Object.keys(product.available_attributes).length > 0 &&
            Object.keys(product.available_attributes).map((attribute) => {
              return handleAttribute(attribute, product.available_attributes[attribute]);
            })}
        </Stack>
      </Stack>
    </Stack>
  );
}

function productAlreadyInPack(product: Product, pack: Pack) {
  return pack.products.some((packProduct) => packProduct.id == product.id);
}
